// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Spinner (M1-16, PH-A22).
//
// A vane hanging from a shaft across a lane. The ball passes underneath it, the vane whips
// over the top, and a switch closes once per revolution — the machine-gun rattle that is
// one of the most satisfying sounds in pinball, and the reason players aim for a spinner
// lane at all.
//
// The vane is modelled from what it physically is, not from an abstract inertia figure:
// a thin flap of known mass hanging a known distance below its shaft. The ball strikes it
// at its own equator, so the lever arm is `shaftHeight − ballRadius`, and gravity gives the
// hanging flap a genuine pendulum restoring torque. That last term costs almost nothing at
// speed — over a full revolution it averages out — but it dominates at the end of a rip,
// which is precisely why a real spinner slows, hesitates, and swings to a stop rather than
// coasting to a halt.
//
// **The inertia figure in `04 §7` is wrong by two orders of magnitude.** `0.00004 kg·m²`
// against a lever arm of 16.5 mm gives the vane an effective mass of 147 g — nearly twice
// the ball — so a ball would lose two thirds of its speed ripping a spinner. A real vane is
// about 4 g of plastic, giving roughly `1.0 × 10⁻⁶ kg·m²`. Authors therefore supply mass
// and height here, which are measurable with a ruler and a kitchen scale, rather than a
// moment of inertia, which is not.

import type { Ball } from '../Ball'
import type { World } from '../World'
import { Device, type DeviceState } from './Device'
import { EV_SPINNER_REVOLUTION } from '../events'
import { A_NORMAL, BALL_RADIUS, MAX_BALLS } from '../constants'
import { PI, TWO_PI, sin } from '../../math/deterministic'

export interface SpinnerConfig {
  readonly id: string
  /** The shaft, projected onto the playfield. The ball crosses it perpendicularly. */
  readonly a: readonly [number, number]
  readonly b: readonly [number, number]
  /** Vane mass, kg. A moulded plastic vane is 3–5 g. */
  readonly vaneMass?: number
  /** Height of the shaft above the playfield, metres. */
  readonly shaftHeight?: number
  /** Length of the vane hanging below the shaft, metres. Nearly reaches the playfield. */
  readonly vaneDrop?: number
  /** Viscous damping at the bearing, per second. */
  readonly damping?: number
  /** Height band in which the ball can reach the vane. */
  readonly zRange?: readonly [number, number]
}

export class Spinner extends Device {
  readonly kind = 'spinner'
  override readonly wantsObserve = true

  readonly ax: number
  readonly ay: number
  readonly bx: number
  readonly by: number
  /** Unit normal to the shaft, in the playfield plane. Positive rotation pushes this way. */
  readonly nx: number
  readonly ny: number

  private readonly length: number
  private readonly vaneMass: number
  private readonly vaneDrop: number
  private readonly leverArm: number
  private readonly inertia: number
  private readonly effectiveMass: number
  private readonly gravityTorque: number
  private readonly damping: number
  private readonly zMin: number
  private readonly zMax: number

  /** Vane angle from hanging straight down, radians. Unbounded — it spins. */
  angle = 0
  /**
   * The angle at the end of the previous step. Written by the simulation, read by renderers.
   *
   * A ripped spinner passes 40 rev/s, which is 0.25 rad per simulation step and a quarter of a
   * turn per frame at 60 Hz. Drawn from `angle` alone the vane strobes — it appears to crawl
   * backwards, or to stand still, depending on how the two rates happen to beat. PH-022 says
   * the renderer interpolates between the two states the fixed step produced, and this is the
   * second of them; without it a spinner is the one part of the machine that cannot obey the
   * rule. Costs one assignment per step and changes nothing about the dynamics.
   */
  prevAngle = 0
  /** Angular velocity, rad/s. */
  omega = 0
  /** Switch closures since the last reset, in either direction. */
  revolutions = 0

  /** Signed count of passes over top dead centre, where the switch lives. */
  private topCrossings = 0
  /** Ball credited with the current spin, for the event's ball field. */
  private lastBall = -1

  protected override ownState(): DeviceState {
    // `side` for the same reason as the gate: it is where each ball was relative to the blade
    // last step, and a restore without it either misses the next crossing or invents one.
    return {
      angle: this.angle,
      prevAngle: this.prevAngle,
      omega: this.omega,
      revolutions: this.revolutions,
      topCrossings: this.topCrossings,
      lastBall: this.lastBall,
      side: [...this.side],
    }
  }

  protected override restoreOwn(state: DeviceState): void {
    this.angle = Number(state['angle'])
    this.prevAngle = Number(state['prevAngle'])
    this.omega = Number(state['omega'])
    this.revolutions = Number(state['revolutions'])
    this.topCrossings = Number(state['topCrossings'])
    this.lastBall = Number(state['lastBall'])
    const side = state['side']
    if (Array.isArray(side)) this.side.set(side as number[])
  }
  private readonly side = new Int8Array(MAX_BALLS)

  constructor(config: SpinnerConfig) {
    super(config.id)
    this.ax = config.a[0]
    this.ay = config.a[1]
    this.bx = config.b[0]
    this.by = config.b[1]
    this.setOrigin((this.ax + this.bx) / 2, (this.ay + this.by) / 2)

    const ex = this.bx - this.ax
    const ey = this.by - this.ay
    this.length = Math.sqrt(ex * ex + ey * ey)
    if (this.length === 0) throw new Error(`Spinner ${config.id}: degenerate shaft`)
    this.nx = -ey / this.length
    this.ny = ex / this.length

    this.vaneMass = config.vaneMass ?? 0.004
    const shaftHeight = config.shaftHeight ?? 0.03
    this.vaneDrop = config.vaneDrop ?? shaftHeight - 0.002
    this.damping = config.damping ?? 1.8
    this.zMin = config.zRange ? config.zRange[0] : -Infinity
    this.zMax = config.zRange ? config.zRange[1] : Infinity

    this.leverArm = shaftHeight - BALL_RADIUS
    if (this.leverArm <= 0) {
      throw new Error(
        `Spinner ${config.id}: shaft at ${shaftHeight} m is at or below the ball's equator, so the ball could never turn it`,
      )
    }

    // Thin flap hinged along its top edge: I = m·L²/3, centre of mass at L/2.
    this.inertia = (this.vaneMass * this.vaneDrop * this.vaneDrop) / 3
    this.effectiveMass = this.inertia / (this.leverArm * this.leverArm)
    this.gravityTorque = this.vaneMass * A_NORMAL * (this.vaneDrop * 0.5)
  }

  override step(world: World, dt: number): void {
    this.prevAngle = this.angle
    // Pendulum term. Negligible while it is ripping, decisive as it dies.
    this.omega -= ((this.gravityTorque * sin(this.angle)) / this.inertia) * dt
    this.omega -= this.omega * this.damping * dt
    this.angle += this.omega * dt

    // Counted here rather than on ball contact, because most of a spinner's revolutions
    // happen *after* the ball has left the lane. That coast is where the score is.
    this.countRevolutions(world)
  }

  override observe(world: World, ball: Ball): void {
    if (ball.z < this.zMin || ball.z > this.zMax) return

    const rel = (ball.x - this.ax) * this.nx + (ball.y - this.ay) * this.ny
    const now: number = rel >= 0 ? 1 : -1
    const was = this.side[ball.id]!
    this.side[ball.id] = now
    if (was !== 0 && was !== now && this.crossedWithinShaft(ball)) {
      this.lastBall = ball.id
      this.strike(ball)
      // Credit the ball's own crossing immediately, rather than a step later.
      this.countRevolutions(world)
    }
  }

  /**
   * The ball drives the vane to match its own speed at the contact point.
   *
   * Not a bounce — a spinner does not send the ball back, it takes a toll. With a 4 g vane
   * on a 16.5 mm lever the effective mass is under 4 g against the ball's 80.6 g, so the
   * toll is around 4% of the crossing speed.
   */
  private strike(ball: Ball): void {
    const vn = ball.vx * this.nx + ball.vy * this.ny
    const surface = this.omega * this.leverArm
    const approach = vn - surface
    if (approach === 0) return

    const j = -approach / (ball.invMass + 1 / this.effectiveMass)
    ball.vx += j * this.nx * ball.invMass
    ball.vy += j * this.ny * ball.invMass
    this.omega -= (j * this.leverArm) / this.inertia
  }

  private crossedWithinShaft(ball: Ball): boolean {
    const ex = (this.bx - this.ax) / this.length
    const ey = (this.by - this.ay) / this.length
    const u = (ball.x - this.ax) * ex + (ball.y - this.ay) * ey
    return u >= -ball.radius && u <= this.length + ball.radius
  }

  /**
   * The switch closes as the vane passes **top dead centre**, once per revolution.
   *
   * Counting `|Δangle| / 2π` instead would be simpler and wrong: a dying vane wobbling
   * either side of the bottom would rack up phantom closures without ever going over the
   * top. Anchoring the count to a physical position means a wobble scores nothing, a full
   * turn scores exactly one, and a reversal un-counts correctly — all from one `floor`.
   */
  private countRevolutions(world: World): void {
    const crossings = Math.floor((this.angle + PI) / TWO_PI)
    while (this.topCrossings < crossings) {
      this.topCrossings++
      this.tick(world, 1)
    }
    while (this.topCrossings > crossings) {
      this.topCrossings--
      this.tick(world, -1)
    }
  }

  /**
   * One switch closure, carrying the speed it happened at.
   *
   * `SimEvent.value` is documented as a magnitude that "drives sound gain and haptic strength,
   * so it must come out of the simulation rather than be guessed by the renderer" — and this
   * was sending ±1, which is the one thing a magnitude is not. A spinner ripping at forty
   * revolutions a second and one wobbling to a halt are the same event with very different
   * numbers, and the renderer had no way to tell them apart.
   *
   * Signed angular velocity keeps the direction the sign always carried and adds the speed the
   * field asked for. `direction` rather than the sign of `omega` because a step can process a
   * reversal, and the crossing's own direction is the one that closed the switch. No rule reads
   * this value — `SwitchMatrix` maps the event to a switch id and ignores it — so nothing about
   * scoring changes.
   */
  private tick(world: World, direction: number): void {
    this.revolutions++
    const speed = this.omega < 0 ? -this.omega : this.omega
    world.events.emit(
      EV_SPINNER_REVOLUTION,
      this.index,
      this.lastBall,
      world.stepIndex,
      direction * speed,
    )
  }

  override reset(): void {
    this.angle = 0
    this.prevAngle = 0
    this.omega = 0
    this.revolutions = 0
    this.topCrossings = 0
    this.lastBall = -1
    this.side.fill(0)
  }
}
